/**
 * AuthCallbackPage — where the realm sends the browser back with the code.
 *
 * Completes the exchange, then goes to the page the sign-in was started
 * from. A realm that answers with `error=` (a Cancel at Google, an expired
 * login) or a failed exchange shows why and offers to start over.
 */
// Not one of the four page shapes: the sign-in callback renders outside the shell, before
// there is a rail, a panel, or a measure to keep (Feature 70, §6.1).
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { completeSignIn, signIn } from '../../auth/oidc.js';
import { Button } from '../../ui/Button.js';
import { Spinner } from '../../ui/Spinner.js';
import { AuthFrame } from './AuthFrame.js';

export function AuthCallbackPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const realmError = params.get('error');
  const [error, setError] = useState<string | null>(
    realmError ? (params.get('error_description') ?? realmError) : null,
  );

  useEffect(() => {
    // The realm already said no; there is no code to exchange.
    if (realmError) return;
    let cancelled = false;
    completeSignIn()
      .then((returnTo) => {
        if (!cancelled) navigate(returnTo, { replace: true });
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [navigate, realmError]);

  return (
    <AuthFrame>
      {error === null ? (
        <div
          role="status"
          aria-live="polite"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 11,
            borderRadius: 'var(--radius-input)',
            background: 'var(--surface-card)',
            padding: 30,
          }}
        >
          <Spinner size={28} />
          <div style={{ fontSize: 'var(--text-body-md)', color: 'var(--text-muted)' }}>
            Signing you in…
          </div>
        </div>
      ) : (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 11,
            borderRadius: 'var(--radius-input)',
            background: 'var(--surface-card)',
            padding: 16,
          }}
        >
          <div
            role="alert"
            style={{
              borderRadius: 'var(--radius-chip)',
              border: '1px solid var(--danger-line)',
              background: 'var(--danger-tint)',
              paddingLeft: 11,
              paddingRight: 11,
              paddingTop: 7,
              paddingBottom: 7,
              fontSize: 'var(--text-body-md)',
              color: 'var(--danger)',
            }}
          >
            Sign-in could not be completed: {error}
          </div>
          <Button variant="primary" onClick={() => void signIn()}>
            Sign in again
          </Button>
        </div>
      )}
    </AuthFrame>
  );
}
